/**
 * The train.
 *
 * A subway platform is a place where something arrives. The arena so far is
 * scenery you can be thrown into; this is the first part of it with a clock,
 * and the only thing in the game that moves without anybody deciding it should.
 *
 * Three phases on a loop:
 *
 *   away         nothing in the tunnel. Most of the cycle.
 *   approaching  the telegraph. Headlights in the tunnel mouth and a sound cue,
 *                and no train yet — this is the whole of the warning, and it is
 *                the reason the pit is a risk you take rather than a coin toss.
 *   passing      the train is in the station: a hard surface, moving.
 *
 * Pure. It holds no three.js and no mesh — it reports where the train is and
 * what phase it is in, and `src/arenas/subwayTrain.js` draws that. The moving
 * `surface` it exposes is a plain object the collision already understands, so
 * a train is a hard surface the same way a pillar is, and nothing in
 * `surfaces.js` had to learn what a train is.
 *
 * It alternates direction each run, because a station has two tunnels and
 * always arriving from the left would read as a loop rather than as a service.
 */

export const TRAIN = Object.freeze({
  /** Seconds of empty platform between services. */
  interval: 13,
  /** Seconds of headlights and noise before the train is actually in the pit. */
  telegraph: 3.2,
  /** Metres per second. A train pulling through a station without stopping. */
  speed: 9,
  /** Metres. Long enough that passing takes a moment rather than a frame. */
  length: 17,
  /** Metres across, the footprint the collision uses. */
  width: 2.3,
  /** Where along z the train's centre line runs. */
  z: 4,
  /** How far past each end of the pit it starts and finishes. */
  runout: 11,
  /** The phase it starts the round in. `away` gives the fighters a moment. */
  initial: 'away',
});

export const PHASES = Object.freeze(['away', 'approaching', 'passing']);

/** Cues a sound layer can subscribe to. There is no audio yet; the hooks are. */
export const CUES = Object.freeze(['approach', 'arrive', 'depart']);

export class TrainConfigError extends Error {}

export function validateTrain(config) {
  for (const key of ['interval', 'telegraph', 'speed', 'length', 'width']) {
    if (!(config[key] > 0)) {
      throw new TrainConfigError(`train needs a positive ${key}, got ${config[key]}`);
    }
  }
  if (config.initial && !PHASES.includes(config.initial)) {
    throw new TrainConfigError(`train cannot start in "${config.initial}"`);
  }
  return config;
}

/**
 * `pit` is the arena's track pit — `minX`/`maxX` are the span the train has to
 * cross, and everything else about it is the pit's business rather than this
 * module's.
 */
export function createTrain({ pit, random = Math.random, ...overrides } = {}) {
  const config = validateTrain({ ...TRAIN, ...overrides });
  const listeners = new Set();
  const cueListeners = new Set();

  const minX = pit?.minX ?? -8;
  const maxX = pit?.maxX ?? 8;
  /** The distance the nose travels from out of sight to out of sight again. */
  const run = maxX - minX + config.runout * 2 + config.length;
  const crossing = run / config.speed;

  let phase = config.initial ?? 'away';
  let elapsed = 0;
  /** +1 runs west to east, -1 the other way. Flipped after every service. */
  let direction = random() < 0.5 ? 1 : -1;

  /**
   * The footprint, as a surface the collision already understands. One object,
   * reused: its `center.x` is rewritten every frame while the train is in the
   * station, and the fight holds a reference to it rather than a copy.
   *
   * It is *hard*, and it costs more than any wall in the arena. Being caught by
   * a train is not the same kind of event as being put through tile.
   */
  const surface = {
    id: 'train',
    shape: 'box',
    hard: true,
    moving: true,
    damage: config.damage ?? 34,
    knockback: config.knockback ?? 0.55,
    stun: config.stun ?? 96,
    center: { x: 0, z: config.z },
    halfX: config.length / 2,
    halfZ: config.width / 2,
  };

  /** Where the train's centre is at `progress` through its crossing, 0 to 1. */
  function centerAt(progress) {
    const from = direction > 0 ? minX - config.runout - config.length / 2 : maxX + config.runout + config.length / 2;
    return from + direction * run * progress;
  }

  function cue(name, detail = {}) {
    for (const listener of cueListeners) listener({ cue: name, phase, direction, ...detail });
  }

  /**
   * `carry` is the time the frame overshot the transition by.
   *
   * Dropping it would quantise every phase change to a frame boundary, and the
   * train's position is derived from its own clock — so on a slow frame the
   * train would be somewhere other than where the collision says it is, by up
   * to a frame's worth of 9 metres per second.
   */
  function enter(next, carry = 0) {
    const from = phase;
    phase = next;
    elapsed = carry;
    if (next === 'approaching') cue('approach', { seconds: config.telegraph });
    if (next === 'passing') cue('arrive', { seconds: crossing });
    if (from === 'passing') cue('depart');
    // Parked at the start of the run either way. Entering `away` happens after
    // the direction has flipped, so this also stages the train at the far
    // tunnel ready for the next service.
    surface.center.x = centerAt(0);
    for (const listener of listeners) {
      listener({ from, to: next, direction, present: next === 'passing' });
    }
  }

  return {
    config,
    /** The moving hard surface. Live while `present`; stale otherwise. */
    surface,

    get phase() {
      return phase;
    },
    /** True while the train is in the station and its surface should be live. */
    get present() {
      return phase === 'passing';
    },
    get direction() {
      return direction;
    },
    /** Where the middle of the train is, along the track. */
    get position() {
      return surface.center.x;
    },
    /** Seconds until the train is in the station, or null when it is or is not coming. */
    get eta() {
      if (phase === 'away') return config.interval - elapsed + config.telegraph;
      if (phase === 'approaching') return Math.max(0, config.telegraph - elapsed);
      return null;
    },

    /**
     * How bright the headlights in the tunnel mouth should be, 0 to 1.
     *
     * Rises through the telegraph and sits at full while the train is in the
     * station. Squared, so the last second of the warning is worth more than
     * the first two — a light that grows evenly does not read as *approaching*.
     */
    get glow() {
      if (phase === 'approaching') {
        const t = Math.min(1, elapsed / config.telegraph);
        return t * t;
      }
      return phase === 'passing' ? 1 : 0;
    },

    /** How far through its crossing the train is, 0 to 1. */
    get progress() {
      return phase === 'passing' ? Math.min(1, elapsed / crossing) : 0;
    },

    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /** Sound cue hooks: `approach`, `arrive`, `depart`. No audio layer yet. */
    onCue(listener) {
      cueListeners.add(listener);
      return () => cueListeners.delete(listener);
    },

    /** Back to an empty platform, without firing a departure nobody heard. */
    reset() {
      phase = config.initial ?? 'away';
      elapsed = 0;
      surface.center.x = centerAt(0);
      for (const listener of listeners) {
        listener({ from: phase, to: phase, direction, present: false, reset: true });
      }
    },

    /** Brings the next service forward to now. The debug overlay's button. */
    summon() {
      if (phase !== 'away') return false;
      enter('approaching');
      return true;
    },

    update(dt) {
      if (!(dt > 0)) return phase;
      elapsed += dt;

      // Sequential rather than exclusive, so one enormous frame walks the whole
      // cycle instead of getting stuck a phase behind for the rest of the round.
      if (phase === 'away' && elapsed >= config.interval) {
        enter('approaching', elapsed - config.interval);
      }
      if (phase === 'approaching' && elapsed >= config.telegraph) {
        enter('passing', elapsed - config.telegraph);
      }
      if (phase === 'passing') {
        if (elapsed >= crossing) {
          // Flipped before entering, so `away` stages the train at the tunnel
          // it will come out of next time.
          direction = -direction;
          enter('away', elapsed - crossing);
        } else {
          // The position is read off the clock rather than integrated, so a
          // dropped frame cannot leave the train a metre from where the
          // fighters' collision thinks it is.
          surface.center.x = centerAt(elapsed / crossing);
        }
      }
      return phase;
    },
  };
}
